/* eslint-disable max-lines -- Modal is over the 300-line cap; extract the redirect-URI and auth-option sections when next reworked. */
// eslint-disable-next-line @heidi/rules/must-lazy-load-modals -- pre-existing eager modal, surfaced when its drawer import moved to the door; lazy-load follow-up tracked separately
import { CibaWaitingScreen } from '@/components/modal/sign-in-modal/ciba-waiting-screen';
import {
  CompliantLogoBadge,
  getComplianceBadgesForCountry,
  useUserCountry,
} from '@/components/modal/sign-in-modal/compliant-logos';
import { AppleLoginButton } from '@/components/modal/sign-in-modal/components/AppleLoginButton';
import { DBLoginButton } from '@/components/modal/sign-in-modal/components/DBLoginButton';
import { GoogleLoginButton } from '@/components/modal/sign-in-modal/components/GoogleLoginButton';
import { HeidiAppLoginButton } from '@/components/modal/sign-in-modal/components/HeidiAppLoginButton';
import { SignInModalHeader } from '@/components/modal/sign-in-modal/components/SignInModalHeader';
import { useCibaLogin } from '@/components/modal/sign-in-modal/hooks/useCibaLogin';
import { useEmailLoginSubmit } from '@/components/modal/sign-in-modal/hooks/useEmailLoginSubmit';
import { SignInBackground } from '@/components/modal/sign-in-modal/sign-in-background';
import { iframeSafeAuthRoute } from '@/components/modal/sign-in-modal/utils';
import { IS_CIBA_LOGIN_ENABLED } from '@/features/flags/legacy-flags';
import { trackScreenViewed } from '@/lib/analytics/scribe/ui';
import { cn } from '@/lib/cn';
import { getProductImage } from '@/lib/utils';
import { useDesktopAuthStore } from '@/models/desktopAuthSlice';
import { useDesktopLoginPending } from '@/tauri/auth0/use-desktop-login-pending';
import { isTauri, isUsingReactKinde } from '@/tauri/utils';
import { appendAmplitudeCrossDomainParams } from '@/utils/amplitudeCrossDomain';
import { isAppleSignInAvailable } from '@/utils/appConfiguration';
import { appendAttributionParamsToRedirectUrl } from '@/utils/attributionParams';
import { prefixWithLocale } from '@/utils/localeRedirect';
import { appendPaymentChosenHashToRedirectUrl } from '@/utils/paymentChosenRedirectUrl';
import { useTranslations } from '@/utils/translations';
import { HeidiLoader } from '@heidi/design-system/heidi-loader';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { Separator } from '@heidi/design-system/separator';
import { TypographyH3 } from '@heidi/design-system/typography';
import { paymentChosenAtom } from '@heidi/growth/data';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EmailInputField } from './components/EmailInputField';
import { useSignInModalState } from './hooks/useSignInModalState';
import { type SignInModalProps } from './types';

/**
 * Cloud (Kinde) implementation of the email-only sign-in modal. The
 * default export from `sign-in-modal-email-only.tsx` is a dispatcher
 * that selects this on cloud builds and the Keycloak variant on
 * on-prem.
 *
 * Email-only login/signup flow that hits Kinde's `/api/auth/register`
 * endpoint, then redirects to `/scribe/new-user` for profile
 * completion.
 */
function CloudSignInModalEmailOnly(props: SignInModalProps) {
  const router = useRouter();
  const { t, locale } = useTranslations();
  const { isOpen, shouldBlurOverlay } = props;

  // Use shared hook for common state management
  const {
    control,
    email,
    isEmailInvalid,
    setError,
    clearErrors,
    emailConnectionId,
    forceUpdateActive,
    postLoginRedirectUri,
    isUsingSsoOrg,
    isFromMobileWebview,
  } = useSignInModalState({
    postLoginRedirectUri: props.postLoginRedirectUri,
  });

  // Global disabled state - set when any login button is clicked
  const [isLoginInProgress, setIsLoginInProgress] = useState(false);

  // Desktop sign-in resolves in place (no navigation), so this shared disable
  // must release when the attempt settles — login success, auth error, launch
  // failure, or the abandonment watchdog — or one failed attempt would strand
  // every button on this modal (MUL-3346). No-op on web: the pending flag is
  // only ever set by the desktop login funnel.
  useDesktopLoginPending(() => setIsLoginInProgress(false));

  // On onboarding route, forward paymentChosen in redirect URL hash (separate from attribution params)
  const paymentChosen = useAtomValue(paymentChosenAtom);

  const handleEmailLoginClick = useCallback(() => {
    if (!email) {
      setError('email', { message: 'auth.emailInvalid' });
      return;
    }
    setIsLoginInProgress(true);
  }, [email, setError]);

  const handleSocialLoginClick = () => {
    clearErrors('email');
    setIsLoginInProgress(true);
  };

  // Compute full redirect URIs with locale prefix, attribution params, and Amplitude cross-domain tracking
  const getFullRedirectUri = useCallback(
    (defaultPath: string) => {
      const path = postLoginRedirectUri ?? defaultPath;
      const withLocale = prefixWithLocale(locale, path);
      const withAttribution = appendAttributionParamsToRedirectUrl(withLocale);
      return appendAmplitudeCrossDomainParams(withAttribution);
    },
    [locale, postLoginRedirectUri]
  );

  // Social logins redirect to /scribe by default
  const socialLoginRedirectUri = useMemo(() => {
    const uri = getFullRedirectUri('/scribe');
    return props.isOnboardingRoute
      ? appendPaymentChosenHashToRedirectUrl(uri, paymentChosen)
      : uri;
  }, [getFullRedirectUri, props.isOnboardingRoute, paymentChosen]);

  // DB login redirects to /scribe/new-user for the unified flow
  const dbLoginRedirectUri = useMemo(() => {
    const uri = getFullRedirectUri('/scribe/new-user');
    return props.isOnboardingRoute
      ? appendPaymentChosenHashToRedirectUrl(uri, paymentChosen)
      : uri;
  }, [getFullRedirectUri, props.isOnboardingRoute, paymentChosen]);

  const {
    status: cibaStatus,
    matchCode: cibaMatchCode,
    expiresAtMs: cibaExpiresAtMs,
    initiate: initiateCiba,
    cancel: cancelCiba,
  } = useCibaLogin();
  const showCibaWaiting = cibaStatus !== 'idle' && cibaStatus !== 'initiating';
  const showHeidiAppLogin = IS_CIBA_LOGIN_ENABLED() && !isFromMobileWebview && !isTauri();

  useEffect(() => {
    if (cibaStatus === 'approved') void router.push(socialLoginRedirectUri);
  }, [cibaStatus, router, socialLoginRedirectUri]);

  const { isDetermineLoginEnabled, submitViaDetermineLogin } = useEmailLoginSubmit({
    email,
    postLoginRedirectUri: dbLoginRedirectUri,
    emailConnectionId,
  });

  const handlePasswordLoginSubmit = useCallback(() => {
    // Use feature flag for production
    // OR
    // If the user is on a mobile webview we use bypass the feature flag and use determine login directly since we can't open the feature flag during the test
    if (isDetermineLoginEnabled || isFromMobileWebview) {
      if (!email || isEmailInvalid) {
        setError('email', { message: 'auth.emailInvalid' });
        return;
      }
      handleEmailLoginClick();
      void submitViaDetermineLogin()
        .catch(() => {
          setIsLoginInProgress(false);
        })
        .finally(() => {
          // Desktop mirror of DBLoginButton's release: a broker error /
          // Kinde fallback RESOLVES without ever marking a pending attempt,
          // so neither the catch above nor the pending falling edge would
          // release the shared disable on this Enter-key submit path.
          if (isTauri() && useDesktopAuthStore.getState().loginPendingSince === null) {
            setIsLoginInProgress(false);
          }
        });
      return;
    }

    if (isUsingReactKinde()) {
      return;
    }

    const url = new URL('/api/auth/register', window.location.origin);
    url.searchParams.set('login_hint', email);
    url.searchParams.set('post_login_redirect_url', dbLoginRedirectUri);
    url.searchParams.set('connection_id', emailConnectionId);
    iframeSafeAuthRoute(url.toString());
  }, [
    isDetermineLoginEnabled,
    isFromMobileWebview,
    email,
    isEmailInvalid,
    setError,
    handleEmailLoginClick,
    submitViaDetermineLogin,
    dbLoginRedirectUri,
    emailConnectionId,
  ]);

  const handleSubmit = useCallback(() => {
    if (!email || isEmailInvalid) {
      setError('email', { message: 'auth.emailInvalid' });
      return;
    }

    handlePasswordLoginSubmit();
  }, [email, isEmailInvalid, setError, handlePasswordLoginSubmit]);

  const hasAutoSubmitted = useRef(false);

  // if the user is on a mobile webview and the email is valid, we auto-submit the form
  useEffect(() => {
    if (isFromMobileWebview && email && !isEmailInvalid && !hasAutoSubmitted.current) {
      hasAutoSubmitted.current = true;
      setIsLoginInProgress(true);
      handleSubmit();
    }
  }, [isFromMobileWebview, email, isEmailInvalid, handleSubmit]);

  // Track screen_viewed when the modal is shown
  useEffect(() => {
    if (isOpen && !isUsingSsoOrg) {
      const screenName = props.isOnboardingRoute
        ? ScreenName.AuthFormSignup
        : ScreenName.AuthForm;
      trackScreenViewed({
        screen_name: screenName,
        url: router.asPath,
      });
    }
  }, [isOpen, isUsingSsoOrg, props.isOnboardingRoute, router.asPath]);

  const productImage = getProductImage();

  const userCountry = useUserCountry();
  const complianceBadges = getComplianceBadgesForCountry(userCountry);

  const overlayClassName = useMemo(() => {
    if (props.signupSource === 'evidence') {
      return shouldBlurOverlay ? 'bg-black/30 backdrop-blur-[3px]' : undefined;
    }
    return productImage;
  }, [props.signupSource, shouldBlurOverlay, productImage]);

  // Don't render SignInModal at all if force update is active
  if (forceUpdateActive) {
    return null;
  }

  return (
    <>
      {isFromMobileWebview &&
        isLoginInProgress &&
        createPortal(
          <div className="fixed inset-0 z-999 flex items-center justify-center bg-surface-page">
            <HeidiLoader />
          </div>,
          document.body
        )}
      <DialogDrawer open={isOpen && !isUsingSsoOrg} onOpenChange={props.onOpenChange}>
        <DialogDrawerContent
          aria-describedby={undefined}
          className={cn(
            'grid max-w-lg gap-0 border-none p-0 sm:w-10/12',
            props.isOnboardingRoute && 'xl:max-w-[943px] xl:grid-cols-2'
          )}
          hideCloseIcon
          overlayClassName={overlayClassName}
          dismissible={props.dismissible}
          data-testid="sign-in-modal"
        >
          {props.isOnboardingRoute && (
            <div
              className={
                'relative hidden flex-col items-center justify-center overflow-hidden rounded-l-2xl xl:flex'
              }
            >
              <div className={'z-10 flex flex-col items-center justify-center gap-7'}>
                <TypographyH3 className="text-center text-text-secondary">
                  {t('auth.yourPatientsPrivacyProtected')}
                </TypographyH3>
                <div className={'flex flex-wrap justify-center gap-4'}>
                  {complianceBadges.map((badge, index) => (
                    <CompliantLogoBadge
                      key={index}
                      iconSrc={badge.iconSrc}
                      alt={badge.alt}
                      text={t(badge.text)}
                    />
                  ))}
                </div>
              </div>
              <SignInBackground
                className={'absolute inset-0 size-full object-cover'}
                alt=""
              />
            </div>
          )}
          <div className={'flex flex-col gap-6 p-8'}>
            <DialogDrawerTitle className="sr-only">{t('signup.title')}</DialogDrawerTitle>
            {showCibaWaiting ? (
              <CibaWaitingScreen
                status={cibaStatus}
                matchCode={cibaMatchCode}
                expiresAtMs={cibaExpiresAtMs}
                onCancel={() => {
                  cancelCiba();
                  setIsLoginInProgress(false);
                }}
              />
            ) : (
              <>
                <SignInModalHeader />
                <form
                  className="flex w-full flex-col items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit();
                  }}
                >
                  <div className="flex w-full flex-col gap-8">
                    <EmailInputField
                      control={control}
                      isOnboardingRoute={props.isOnboardingRoute}
                    />
                    <div className="flex w-full flex-col gap-2">
                      <DBLoginButton
                        email={email}
                        isEmailInvalid={isEmailInvalid}
                        onClick={handleEmailLoginClick}
                        // Desktop: a click that ends without a launchable
                        // attempt (broker error / Kinde fallback) never
                        // marks the shared pending flag, so the
                        // pending-settle release above would not fire —
                        // release the shared disable here instead.
                        onAttemptAbandoned={() => setIsLoginInProgress(false)}
                        disabled={isLoginInProgress}
                        postLoginRedirectUri={dbLoginRedirectUri}
                        emailConnectionId={emailConnectionId}
                      />

                      <div className="flex w-full items-center gap-6">
                        <Separator className="flex-1" />
                        <span className="text-sm text-text-secondary">
                          {t('auth.or')}
                        </span>
                        <Separator className="flex-1" />
                      </div>

                      {showHeidiAppLogin && (
                        <HeidiAppLoginButton
                          email={email}
                          isEmailInvalid={isEmailInvalid}
                          disabled={isLoginInProgress}
                          onInvalidEmail={() =>
                            setError('email', { message: 'auth.emailInvalid' })
                          }
                          onProceed={() => {
                            clearErrors('email');
                            setIsLoginInProgress(true);
                            void initiateCiba(email);
                          }}
                        />
                      )}

                      {/* onLoginFailed: a desktop launch failure may mark and
                          clear the pending attempt within one tick, so the
                          falling-edge release above cannot be relied on for
                          this path — release the shared disable explicitly. */}
                      <GoogleLoginButton
                        onClick={handleSocialLoginClick}
                        onLoginFailed={() => setIsLoginInProgress(false)}
                        disabled={isLoginInProgress}
                        postLoginRedirectUri={socialLoginRedirectUri}
                      />

                      {isAppleSignInAvailable() && (
                        <AppleLoginButton
                          onClick={handleSocialLoginClick}
                          onLoginFailed={() => setIsLoginInProgress(false)}
                          disabled={isLoginInProgress}
                          postLoginRedirectUri={socialLoginRedirectUri}
                        />
                      )}
                    </div>
                  </div>
                </form>
              </>
            )}
          </div>
        </DialogDrawerContent>
      </DialogDrawer>
    </>
  );
}

export default CloudSignInModalEmailOnly;
